import { useEffect, type ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { Globe2, LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { fetchAccountDomains, type AccountDomain } from "./api";

export function Domains(): ReactElement {
  const domainsQuery = useQuery({
    queryKey: ["account-domains"],
    queryFn: fetchAccountDomains,
    retry: false,
  });

  useEffect(() => {
    const previous = document.title;
    document.title = "Domains — Chill";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-12">
      <header className="border-b border-[#cfe4f1] pb-7">
        <h1 className="font-['Avenir_Next',Inter,sans-serif] text-3xl font-semibold tracking-[-0.045em] sm:text-[2.5rem]">
          Domains
        </h1>
        <p className="mt-2 max-w-xl text-sm leading-6 text-[#68736e]">
          See every domain your account owns or uses, and where it is connected.
        </p>
      </header>

      {domainsQuery.isPending ? (
        <div className="flex min-h-48 items-center justify-center text-[#66736e]">
          <LoaderCircle className="size-5 animate-spin" aria-label="Loading domains" />
        </div>
      ) : domainsQuery.isError ? (
        <div className="mt-8 rounded-2xl border border-[#ead6cf] bg-[#fffaf7] px-5 py-5">
          <h2 className="font-semibold">Couldn't load your domains</h2>
          <p className="mt-1 text-sm text-[#775f57]">{domainsQuery.error.message}</p>
          <Button
            type="button"
            variant="outline"
            className="mt-4"
            onClick={() => void domainsQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : domainsQuery.data.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-[#bcd9ea] bg-white/35 px-6 py-14 text-center">
          <Globe2 className="mx-auto size-6 text-[#5b7890]" aria-hidden="true" />
          <h2 className="mt-4 font-semibold">No domains yet</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-[#6f7974]">
            Domains you register with Chill or connect to a website will appear here.
          </p>
        </div>
      ) : (
        <section className="mt-8 overflow-hidden rounded-2xl border border-[#cfe4f1] bg-white/88 shadow-[0_16px_44px_rgba(91,139,174,0.08)] backdrop-blur-sm" aria-label="Your domains">
          <div className="divide-y divide-[#dcebf4]">
            {domainsQuery.data.map((domain) => (
              <DomainRow key={domain.domain} domain={domain} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
}

function DomainRow({ domain }: { domain: AccountDomain }): ReactElement {
  const status = domainStatus(domain.status);
  return (
    <article className="grid gap-4 px-5 py-5 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:px-6">
      <div className="flex min-w-0 items-start gap-3.5">
        <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#dceffc] text-[#47677d]">
          <Globe2 className="size-4" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h2 className="truncate font-medium">{domain.domain}</h2>
          <p className="mt-1 text-xs leading-5 text-[#737d78]">
            {domain.ownership === "registered"
              ? "Registered with Chill"
              : "Connected from another provider"}
            {domain.expiresAt ? ` · Through ${formatDate(domain.expiresAt)}` : ""}
          </p>
          {domain.attachedProject ? (
            <a
              href={`/workspaces/${encodeURIComponent(domain.attachedProject.id)}`}
              className="mt-1 inline-block text-xs font-medium text-[#4f5f8e] underline-offset-4 hover:underline"
            >
              {domain.attachedProject.name}
            </a>
          ) : (
            <p className="mt-1 text-xs text-[#8a928e]">Not connected to a project</p>
          )}
        </div>
      </div>
      <span
        className={cn(
          "w-fit rounded-full px-2.5 py-1 text-xs font-medium",
          status.tone,
        )}
      >
        {status.label}
      </span>
    </article>
  );
}

function domainStatus(status: AccountDomain["status"]): {
  label: string;
  tone: string;
} {
  if (status === "active") {
    return { label: "Active", tone: "bg-[#e1efe9] text-[#315f54]" };
  }
  if (status === "connected") {
    return { label: "Connected", tone: "bg-[#e8f2f8] text-[#466778]" };
  }
  if (status === "submitting") {
    return { label: "Setting up", tone: "bg-[#ececf6] text-[#5b5e85]" };
  }
  return { label: "Needs attention", tone: "bg-[#f7e9e2] text-[#8a4d3d]" };
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(value));
}
